import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStatus } from '@/hooks/api/useOnboarding';

const ONBOARDING_STORAGE_KEY = 'econsciente_onboarding_dismissed';

export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const { data: onboardingStatus, isLoading } = useOnboardingStatus();

  useEffect(() => {
    if (!isLoading && onboardingStatus) {
      // Verifica se usuário já pulou/completou o onboarding
      const dismissed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (dismissed === 'true') {
        return;
      }

      // Se já completou o onboarding via API, não mostra
      if (onboardingStatus.completed) {
        localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
        return;
      }

      // Atualiza step baseado no progresso
      const steps = onboardingStatus.steps;
      if (!steps.profilePic && !steps.bio && !steps.firstChallenge) {
        setCurrentStep(0);
      } else if (steps.profilePic && !steps.bio) {
        setCurrentStep(2);
      } else if (steps.profilePic && steps.bio && !steps.firstChallenge) {
        setCurrentStep(3);
      }

      // Mostra o onboarding se não estiver completo e não foi pulado
      setIsVisible(true);
    }
  }, [isLoading, onboardingStatus]);

  if (isLoading || !isVisible) {
    return null;
  }

  const steps = [
    {
      title: 'Bem-vindo ao EcoNsciente! 🌱',
      description: 'Faça a diferença no planeta e ganhe pontos por suas ações sustentáveis!',
      icon: '🌍',
      action: null,
    },
    {
      title: 'Configure seu Perfil 📸',
      description: 'Adicione uma foto de perfil e ganhe +100 pontos!',
      icon: '👤',
      action: () => navigate('/EditarPerfil'),
      points: 100,
    },
    {
      title: 'Conte sua História ✍️',
      description: 'Adicione uma biografia e ganhe +50 pontos!',
      icon: '📝',
      action: () => navigate('/EditarPerfil'),
      points: 50,
    },
    {
      title: 'Complete seu Primeiro Desafio 🎯',
      description: 'Realize um desafio ecológico e ganhe +200 pontos!',
      icon: '🚀',
      action: () => navigate('/Home'),
      points: 200,
    },
    {
      title: 'Tudo Pronto! 🎉',
      description: 'Você está pronto para começar sua jornada sustentável. Ganhe +50 pontos bônus ao completar tudo!',
      icon: '🏆',
      action: null,
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAction = () => {
    const step = steps[currentStep];
    if (step.action) {
      step.action();
      setIsVisible(false);
    } else if (currentStep === steps.length - 1) {
      // Último passo - marca como completo
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
      setIsVisible(false);
    } else {
      handleNext();
    }
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setIsVisible(false);
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/85 backdrop-blur-lg flex items-center justify-center z-[9999] animate-[fadeIn_0.3s_ease-out]">
      <div className="bg-gradient-to-br from-[#1A211A] to-[#2a3a2a] rounded-3xl p-8 max-w-[500px] w-[90%] shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative animate-[slideUp_0.4s_ease-out] border border-white/10">
        {/* Barra de progresso */}
        <div className="w-full h-1 bg-white/10 rounded-sm overflow-hidden mb-8">
          <div className="h-full bg-gradient-to-r from-[#00A335] to-[#00d444] rounded-sm transition-[width] duration-400 ease-out" style={{ width: `${progress}%` }} />
        </div>

        {/* Conteúdo */}
        <div className="text-center py-4">
          <div className="text-[5rem] mb-6 animate-[bounce_1s_ease-in-out_infinite]">{currentStepData.icon}</div>
          <h2 className="text-[1.8rem] font-bold text-white my-0 mb-4">{currentStepData.title}</h2>
          <p className="text-base text-white/80 leading-relaxed my-0 mb-6">{currentStepData.description}</p>

          {currentStepData.points && (
            <div className="inline-flex items-center gap-2 bg-gradient-to-br from-[#EE9300] to-[#ffa500] py-2 px-6 rounded-[50px] mb-6 animate-[pulse_2s_ease-in-out_infinite]">
              <span className="text-2xl">⭐</span>
              <span className="text-lg font-bold text-white">+{currentStepData.points} pontos</span>
            </div>
          )}

          {/* Indicadores de passos */}
          <div className="flex gap-2 justify-center mt-8">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full bg-white/30 transition-all duration-300 ${index === currentStep ? 'w-6 rounded bg-[#00A335]' : ''} ${index < currentStep ? 'bg-[#00A335]' : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Botões de navegação */}
        <div className="flex justify-between items-center mt-8 gap-4">
          <button
            className="px-4 py-3 bg-transparent text-white/60 border-none cursor-pointer transition-all duration-200 hover:text-white/90"
            onClick={handleSkip}
            data-testid="skip-onboarding"
          >
            Pular tutorial
          </button>

          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                className="px-6 py-3 border-none rounded-xl text-base font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap bg-white/10 text-white border border-white/20 hover:bg-white/15"
                onClick={handlePrevious}
              >
                ← Voltar
              </button>
            )}

            <button
              className="px-6 py-3 border-none rounded-xl text-base font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap bg-gradient-to-br from-[#00A335] to-[#00d444] text-white shadow-[0_4px_16px_rgba(0,163,53,0.3)] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(0,163,53,0.4)]"
              onClick={handleAction}
            >
              {currentStep === steps.length - 1
                ? 'Começar! 🚀'
                : currentStepData.action
                  ? 'Ir para lá →'
                  : 'Próximo →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
